import './PositionsAside.scss';
import React, { useRef } from 'react';
import clsx from 'clsx';
import { MdSearch } from 'react-icons/md';
import { IPosition } from '@models/winnerSelection/ITablePositions';
import { PosInfo } from '../utils/posInfoMerge';
import { AsideList } from './AsideList';
import { AsideCollapseButton } from './AsideToggle';

export type FilterKey = 'all' | 'open' | 'decided';

export interface IFilterCounts {
    all: number;
    open: number;
    decided: number;
}

interface IProps {
    positions: IPosition[] | undefined;
    posInfo: Record<string, PosInfo>;
    filteredPositions: IPosition[];
    counts: IFilterCounts;
    /** True пока chunk'и /offersInfo догружаются (для shimmer + «считаем…»). */
    isOffersInfoLoading: boolean;
    /** Текущий поиск + setter. */
    search: string;
    setSearch: (s: string) => void;
    filter: FilterKey;
    setFilter: (f: FilterKey) => void;
    currentKey: string | undefined;
    onSelect: (p: IPosition) => void;
    onCollapseAside: () => void;
    /** Ref на input — родитель использует для focus при '/' shortcut. */
    searchInputRef: React.RefObject<HTMLInputElement | null>;
}

/**
 * Боковая панель списка позиций: title + progress + search + filter-chips +
 * виртуализированный список + foot с keyboard hints.
 *
 * Большой блок (~150 строк JSX) вынесен из TablePositions целиком.
 * Внутри использует AsideList (виртуализация), CollapseButton (toggle).
 */
export const PositionsAside: React.FC<IProps> = ({
    positions,
    posInfo,
    filteredPositions,
    counts,
    isOffersInfoLoading,
    search,
    setSearch,
    filter,
    setFilter,
    currentKey,
    onSelect,
    onCollapseAside,
    searchInputRef,
}) => {
    const listRef = useRef<HTMLElement>(null);

    return (
        <aside className="ws-aside">
            <div className="ws-aside__head">
                <div className="ws-aside__title">
                    <h2>Позиции</h2>
                    <span className="meta">
                        {positions?.length ?? 0}
                        {(() => {
                            const totalOffers = (positions ?? []).reduce(
                                (sum, p) => sum + (p.offersCount ?? posInfo[p.key]?.offersCount ?? 0),
                                0,
                            );
                            return totalOffers > 0 ? <> · {totalOffers} предл.</> : null;
                        })()}
                    </span>
                    <AsideCollapseButton onClick={onCollapseAside} />
                </div>
                <div
                    className={clsx('ws-aside__progress', { 'is-warming': isOffersInfoLoading })}
                    title="Прогресс назначения победителей"
                >
                    <div className="track">
                        <div
                            className="fill"
                            style={{
                                width: counts.all > 0 ? `${Math.round((counts.decided / counts.all) * 100)}%` : '0%',
                            }}
                        />
                        {isOffersInfoLoading && <div className="shimmer" aria-hidden />}
                    </div>
                    <div className="caption">
                        <span>
                            <b>{counts.decided}</b> завершено
                            {isOffersInfoLoading && (
                                <>
                                    {' '}
                                    <i className="caption__pending">· считаем…</i>
                                </>
                            )}
                        </span>
                        <span>
                            из <b>{counts.all}</b>
                        </span>
                    </div>
                </div>
            </div>

            <div className="ws-aside__search">
                <MdSearch size="14px" />
                <input
                    ref={searchInputRef}
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Поиск по позициям…"
                    aria-label="Поиск по позициям"
                />
                <kbd>/</kbd>
            </div>

            <div className="ws-aside__filters" role="toolbar" aria-label="Фильтр позиций">
                {(
                    [
                        { key: 'all', label: 'Все', count: counts.all },
                        { key: 'open', label: 'Открытые', count: counts.open },
                        { key: 'decided', label: 'С победителем', count: counts.decided },
                    ] as { key: FilterKey; label: string; count: number }[]
                ).map((f) => (
                    <button
                        key={f.key}
                        type="button"
                        className={clsx({ 'is-on': filter === f.key })}
                        onClick={() => setFilter(f.key)}
                    >
                        {f.label} <span className="count">{f.count}</span>
                    </button>
                ))}
            </div>

            <nav className="ws-aside__list" aria-label="Список позиций" ref={listRef}>
                {filteredPositions.length === 0 ? (
                    <div className="ws-aside__empty">Ничего не найдено</div>
                ) : (
                    <AsideList
                        items={filteredPositions}
                        scrollRef={listRef}
                        currentKey={currentKey}
                        onSelect={onSelect}
                        posInfo={posInfo}
                    />
                )}
            </nav>

            <div className="ws-aside__foot">
                <div className="hints">
                    <span>
                        <kbd>↑</kbd>
                        <kbd>↓</kbd> позиции
                    </span>
                    <span>
                        <kbd>/</kbd> поиск
                    </span>
                </div>
            </div>
        </aside>
    );
};
